import { z } from 'zod';
import { CostQuerySchema } from './cockpit.schemas';

export const CoPilotVisibilityQuerySchema = CostQuerySchema.extend({
  costThresholdUsd: z.coerce.number().min(1).optional(),
});

export const CoPilotFeedbackInvestigationsQuerySchema =
  CoPilotVisibilityQuerySchema.extend({
    hasInvestigation: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) =>
        value === 'true' ? true : value === 'false' ? false : undefined,
      ),
    rating: z.enum(['positive', 'negative']).optional(),
  });

export const CoPilotVisibilityCategoryQueriesQuerySchema =
  CoPilotVisibilityQuerySchema.extend({
    category: z.string().min(1),
  });

export const CoPilotClassifiedQuerySchema = z.object({
  query: z.string(),
  tenantName: z.string().optional(),
  threadId: z.string(),
  category: z.string(),
  classifiedAt: z.string(),
});

export const CoPilotVisibilityKpiSchema = z.object({
  totalCostUsd: z.number(),
  meanCostPerThread: z.number(),
  activeThreads: z.number(),
  errorRatePct: z.number(),
  expensiveTracesCount: z.number(),
  uncoveredQueriesCount: z.number(),
  costThresholdUsd: z.number(),
});

export const CoPilotCostDistributionSchema = z.object({
  mean: z.number(),
  median: z.number(),
  min: z.number(),
  max: z.number(),
  p95: z.number(),
  histogram: z.object({
    buckets: z.array(z.string()),
    counts: z.array(z.number()),
  }),
});

export const CoPilotVisibilityCostSchema = z.object({
  overview: z.object({
    byTenant: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        costUsd: z.number(),
      }),
    ),
    byModel: z.array(
      z.object({
        name: z.string(),
        value: z.number(),
      }),
    ),
    trend: z.object({
      categories: z.array(z.string()),
      series: z.array(
        z.object({
          name: z.string(),
          data: z.array(z.number()),
        }),
      ),
    }),
  }),
  distribution: CoPilotCostDistributionSchema,
});

export const ArchitectureFindingsSchema = z.object({
  title: z.string(),
  issues: z.array(z.string()),
  recommendations: z.array(z.string()),
});

export const CoPilotExpensiveTraceSchema = z.object({
  threadId: z.string(),
  tenantId: z.string(),
  tenantName: z.string().optional(),
  applicationId: z.string().nullable(),
  targetType: z.string().nullable(),
  turns: z.number(),
  totalTokens: z.number(),
  costUsd: z.number(),
  durationMs: z.number().nullable(),
  feedback: z.string().nullable(),
  investigatedAt: z.iso.datetime({ offset: true }),
  costSnapshotAt: z.iso.datetime({ offset: true }),
});

export const CoPilotExpensiveTraceDetailSchema =
  CoPilotExpensiveTraceSchema.extend({
    conversationSummary: z.string().nullable(),
    perTurnCost: z.array(z.number()),
    toolsUsed: z.array(z.string()),
    tokenBreakdown: z
      .object({
        input: z.number(),
        output: z.number(),
        reasoning: z.number(),
        cacheRead: z.number(),
        cacheWrite: z.number(),
      })
      .nullable(),
    architectureFindings: ArchitectureFindingsSchema.nullable(),
    langfuseTraceIds: z.array(z.string()),
    langfuseDigest: z.unknown().optional(),
  });

export const CoPilotVisibilityErrorsSchema = z.object({
  periodDeltaPct: z.number(),
  overTime: z.object({
    categories: z.array(z.string()),
    series: z.array(
      z.object({
        name: z.string(),
        data: z.array(z.number()),
      }),
    ),
  }),
  heatmap: z.object({
    tenants: z.array(z.string()),
    apps: z.array(z.string()),
    data: z.array(z.array(z.number())),
  }),
  recent: z.array(
    z.object({
      timestamp: z.string(),
      errorType: z.string(),
      tenantId: z.string(),
      tenantName: z.string().optional(),
      applicationName: z.string().nullable(),
      threadId: z.string().nullable(),
      rootCause: z.string(),
    }),
  ),
});

export const CoPilotVisibilityQueriesSchema = z.object({
  categories: z.array(
    z.object({
      name: z.string(),
      value: z.number(),
    }),
  ),
  uncoveredQueries: z.array(
    z.object({
      query: z.string(),
      tenantName: z.string().optional(),
      threadId: z.string(),
      reason: z.string(),
    }),
  ),
});

export const CoPilotVisibilityCategoryQueriesSchema = z.object({
  category: z.string(),
  total: z.number(),
  queries: z.array(CoPilotClassifiedQuerySchema),
});

export const CoPilotVisibilityPerformanceSchema = z.object({
  successRate: z.object({
    successPct: z.number(),
    failurePct: z.number(),
    successCount: z.number(),
    failureCount: z.number(),
  }),
  feedbackByTenant: z.object({
    categories: z.array(z.string()),
    positive: z.array(z.number()),
    negative: z.array(z.number()),
  }),
  adoption: z.object({
    categories: z.array(z.string()),
    series: z.array(
      z.object({
        name: z.string(),
        data: z.array(z.number()),
      }),
    ),
  }),
  latency: z.object({
    categories: z.array(z.string()),
    values: z.array(z.number()),
  }),
  tokenSplit: z.object({
    categories: z.array(z.string()),
    values: z.array(z.number()),
  }),
  turnsVsCost: z.array(
    z.object({
      turns: z.number(),
      costUsd: z.number(),
    }),
  ),
  toolUsage: z.object({
    names: z.array(z.string()),
    invocations: z.array(z.number()),
    errors: z.array(z.number()),
  }),
});

export type CoPilotVisibilityKpi = z.infer<typeof CoPilotVisibilityKpiSchema>;
export type CoPilotVisibilityCost = z.infer<typeof CoPilotVisibilityCostSchema>;
export type CoPilotVisibilityErrors = z.infer<
  typeof CoPilotVisibilityErrorsSchema
>;
export type CoPilotVisibilityQueries = z.infer<
  typeof CoPilotVisibilityQueriesSchema
>;
export type CoPilotVisibilityCategoryQueries = z.infer<
  typeof CoPilotVisibilityCategoryQueriesSchema
>;
export type CoPilotVisibilityPerformance = z.infer<
  typeof CoPilotVisibilityPerformanceSchema
>;

export const CoPilotFeedbackInvestigationStatusSchema = z.enum([
  'pending',
  'complete',
  'failed',
  'none',
]);

export const CoPilotFeedbackInvestigationRowSchema = z.object({
  feedbackId: z.string(),
  threadId: z.string(),
  traceId: z.string(),
  tenantId: z.string(),
  tenantName: z.string().optional(),
  applicationId: z.string(),
  applicationName: z.string().optional(),
  userId: z.string(),
  submitterName: z.string().optional(),
  rating: z.enum(['positive', 'negative']).nullable(),
  feedbackExcerpt: z.string(),
  createdAt: z.iso.datetime({ offset: true }),
  investigationStatus: CoPilotFeedbackInvestigationStatusSchema,
  investigatedAt: z.iso.datetime({ offset: true }).nullable(),
});

export const CoPilotFeedbackInvestigationDetailSchema =
  CoPilotFeedbackInvestigationRowSchema.extend({
    feedback: z.string(),
    conversationSummary: z.string().nullable(),
    architectureFindings: ArchitectureFindingsSchema.nullable(),
    traceDigestSummary: z.string().nullable(),
    langfuseTraceIds: z.array(z.string()),
    screenshotPath: z.string().nullable(),
    investigationErrorMessage: z.string().nullable(),
  });

export type CoPilotFeedbackInvestigationRow = z.infer<
  typeof CoPilotFeedbackInvestigationRowSchema
>;
export type CoPilotFeedbackInvestigationDetail = z.infer<
  typeof CoPilotFeedbackInvestigationDetailSchema
>;
